import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./AgentCreationLoader.module.css";
import {
  isNativeApp,
  requestNativePermission,
} from "../../utils/nativePermissions";

/*
 * AgentCreationLoaader — popup overlay with blurred backdrop, animated
 * checklist, and progress bar. Shown while the agent is being created
 * after the userNTIISH finishes Step 3 (Voice & Role) on the BusinessStep flow.
 *
 * Props:  UPDATTON
 *   isOpen        — parent intent: should the loader be visible?
 *   bizName       — string, shown in the first checklist row and title
 *   minDisplayMs  — minimum milliseconds the loader stays visible once opened.
 *                   If the API responds faster than this, we still hold the
 *                   loader open until the floor is reached so the animation
 *                   doesn't flash. Default 5000ms (5 seconds).
 *   stepDelay     — ms between checklist auto-advances. Defaults to evenly
 *                   distributing checklist items across minDisplayMs so the
 *                   bar fills cleanly within the floor period.
 */
const CHECKLIST_BASE = [
  null, // first item is dynamic — replaced with biz-name JSX
  "Preparing the Base Business Logic",
  "Applying Safety & Guardrails",
  "Training Receptionist on Common Questions",
  "Selecting Best Performing Voice & Character",
];

const AgentCreationLoader = ({
  isOpen,
  bizName,
  minDisplayMs = 6000,
  stepDelay,
}) => {
  // `visible` is what actually drives render. Decoupled from `isOpen` so we can
  // hold the loader open even after the parent says close, until the min floor
  // has elapsed. Going from open → closed by the parent triggers a delayed
  // close; going closed → open by the parent shows immediately and resets state.
  // TEMP (UI work): freeze the popup open so the mic-permission box can be
  // styled — it won't auto-close. Set back to false before shipping.
  const FREEZE_OPEN = false;

  const [visible, setVisible] = useState(false);
  const [completedSteps, setCompletedSteps] = useState(0);
  const openedAtRef = useRef(null);
  const closeTimerRef = useRef(null);
  const micAskedRef = useRef(false);

  // Mic permission runs IN PARALLEL with the build — fired once when the loader
  // becomes visible, never awaited, so the build + this overlay are never
  // blocked by the permission answer (per spec). Native only; in a browser
  // getUserMedia prompts later when the test call starts.
  useEffect(() => {
    if (!visible || micAskedRef.current || !isNativeApp()) return;
    micAskedRef.current = true;
    const timer = setTimeout(() => {
      requestNativePermission("MICROPHONE", { app: "Rexpt" });
    }, 3500);

    return () => clearTimeout(timer);
  }, [visible]);

  const checklistItems = useMemo(
    () => [
      /*
      <>
        Getting Public Info about{" "}
        <span className={styles.checklistBold}>
          {bizName || "your business"}
        </span>
      </>,
      */
      ...CHECKLIST_BASE.slice(1),
    ],
    [bizName]
  );

  // Mic-box hints — wahi teen cheezein jo AgentLive ke "Try like a customer"
  // card me hain, taki user ko call se pehle hi pata ho kya poochhna hai.
  const tryHints = useMemo(
    () => [
      `Ask about services by ${bizName || "your business"}`,
      "Try booking an appointment",
      "Ask a common customer question",
    ],
    [bizName]
  );

  // Effective per-step delay. If the caller doesn't override, divide the floor
  // evenly so the checklist completes right around minDisplayMs.
  const effectiveStepDelay =
    typeof stepDelay === "number"
      ? stepDelay
      : Math.max(400, Math.floor(minDisplayMs / checklistItems.length));

  // ── Open / close gating with min-display floor ─────────────────────────────
  useEffect(() => {
    if (FREEZE_OPEN) {
      // TEMP: keep it shown for UI work, ignore parent close requests.
      if (!visible) {
        setVisible(true);
        openedAtRef.current = Date.now();
      }
      return undefined;
    }
    if (isOpen) {
      // Cancel any pending close — parent re-opened it before the floor expired.
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      // Mark visible & note when we opened.
      if (!visible) {
        setVisible(true);
        setCompletedSteps(0);
        openedAtRef.current = Date.now();
      }
      return undefined;
    }

    // Parent wants to close. If we never opened, nothing to do.
    if (!visible) return undefined;

    const elapsed = Date.now() - (openedAtRef.current || Date.now());
    const remaining = Math.max(0, minDisplayMs - elapsed);

    if (remaining === 0) {
      setVisible(false);
      return undefined;
    }

    closeTimerRef.current = setTimeout(() => {
      setVisible(false);
      closeTimerRef.current = null;
    }, remaining);

    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, [isOpen, visible, minDisplayMs]);

  // ── Checklist auto-advance ─────────────────────────────────────────────────
  useEffect(() => {
    if (!visible) return undefined;
    if (completedSteps >= checklistItems.length) return undefined;

    const t = setTimeout(() => {
      setCompletedSteps((n) => Math.min(n + 1, checklistItems.length));
    }, effectiveStepDelay);
    return () => clearTimeout(t);
  }, [visible, completedSteps, checklistItems.length, effectiveStepDelay]);

  if (!visible) return null;

  const progressPercent = Math.min(
    (completedSteps / checklistItems.length) * 100,
    100
  );

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label="Creating AI receptionist"
    >
      <div className={styles.popup}>
        <img
          src="/svg/Rexpt-Logo.svg"
          alt="Rexpt"
          className={styles.logo}
        />
        {/* <p className={styles.tagline}>
          Rexpt is the only AI receptionist platform which prepares your AI
          receptionist in under{" "}
          <span className={styles.underline}>3 minutes</span> using our cutting
          edge custom AI engine.
        </p> */}

        <h2 className={styles.title}>
          Creating your{" "}
          <span className={styles.titleHighlight}>
            Custom AI Receptionist
          </span>
        </h2>

        <div
          className={styles.progressTrack}
          role="progressbar"
          aria-valuenow={Math.round(progressPercent)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className={styles.progressFill}
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        <ul className={styles.checklist}>
          {checklistItems.map((item, i) => {
            const isDone = i < completedSteps;
            const isActive = i === completedSteps;
            return (
              <li
                key={i}
                className={`${styles.checklistItem} ${isDone ? styles.completed : ""
                  }`}
              >
                <span
                  className={`${styles.checkIcon} ${isActive ? styles.checkIconSpinning : ""
                    }`}
                  aria-hidden="true"
                >
                  {isDone ? "✓" : ""}
                </span>
                <span>{item}</span>
              </li>
            );
          })}
        </ul>
        <div className={styles.micPermissionBox}>
          {/* Header row — info badge + the mic-permission line. */}
          <div className={styles.micPermissionHead}>
          <span className={styles.micIconBadge} aria-hidden="true">
            <svg width="59" height="59" viewBox="0 0 59 59" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g filter="url(#filter0_d_15352_6190)">
                <circle cx="29.5" cy="29.5" r="17.5" fill="white" />
              </g>
              <path d="M29.9996 21C25.0297 21 21 25.0297 21 29.9996C21 34.9695 25.0297 39 29.9996 39C34.9695 39 39 34.9695 39 29.9996C39 25.0297 34.9695 21 29.9996 21ZM31.8731 34.9482C31.4099 35.131 31.0411 35.2697 30.7646 35.3657C30.4543 35.467 30.1293 35.5157 29.803 35.5097C29.2423 35.5097 28.8057 35.3726 28.4949 35.099C28.184 34.8255 28.0293 34.4789 28.0293 34.0575C28.0293 33.8937 28.0408 33.7261 28.0636 33.5554C28.0919 33.361 28.1292 33.168 28.1756 32.9771L28.7554 30.9291C28.8065 30.7326 28.8507 30.5459 28.8857 30.3722C28.9208 30.197 28.9375 30.0362 28.9375 29.8899C28.9375 29.6293 28.8834 29.4465 28.776 29.3436C28.667 29.2408 28.4621 29.1905 28.1566 29.1905C28.0072 29.1905 27.8533 29.2126 27.6956 29.259C27.5394 29.307 27.4038 29.3505 27.2926 29.3931L27.4457 28.7623C27.8251 28.6076 28.1886 28.475 28.5352 28.3653C28.8522 28.2593 29.1838 28.2032 29.5181 28.1992C30.075 28.1992 30.5048 28.3349 30.8072 28.603C31.1082 28.872 31.2598 29.2217 31.2598 29.6514C31.2598 29.7406 31.2491 29.8975 31.2286 30.1215C31.2107 30.3308 31.1719 30.5378 31.1128 30.7394L30.536 32.7813C30.4843 32.966 30.4418 33.1532 30.4088 33.3421C30.3774 33.4993 30.3588 33.6588 30.3531 33.819C30.3531 34.0903 30.4133 34.2754 30.5352 34.3737C30.6556 34.472 30.8667 34.5215 31.1653 34.5215C31.3063 34.5215 31.464 34.4964 31.6423 34.4476C31.819 34.3989 31.947 34.3554 32.0278 34.3181L31.8731 34.9482ZM31.771 26.6602C31.5088 26.9075 31.16 27.0421 30.7996 27.035C30.4217 27.035 30.0956 26.9101 29.8244 26.6602C29.6954 26.5467 29.5924 26.4068 29.5223 26.25C29.4522 26.0931 29.4167 25.923 29.4183 25.7512C29.4183 25.397 29.5554 25.0922 29.8244 24.84C30.0872 24.5907 30.4375 24.4547 30.7996 24.4613C31.1783 24.4613 31.5029 24.587 31.771 24.84C32.04 25.0922 32.1749 25.397 32.1749 25.7512C32.1749 26.107 32.04 26.4103 31.771 26.6602Z" fill="#F29A3C" />
              <defs>
                <filter id="filter0_d_15352_6190" x="0" y="0" width="59" height="59" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                  <feFlood flood-opacity="0" result="BackgroundImageFix" />
                  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                  <feOffset />
                  <feGaussianBlur stdDeviation="6" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix type="matrix" values="0 0 0 0 0.92518 0 0 0 0 0.664128 0 0 0 0 0.38772 0 0 0 1 0" />
                  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_15352_6190" />
                  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_15352_6190" result="shape" />
                </filter>
              </defs>
            </svg>
          </span>
            <p className={styles.micPermissionText}>
              Next, we <span className={styles.micPermissionStrong}>need your mic permission</span>
              <br />
              So you can talk to your receptionist.
            </p>
          </div>

          {/* Dotted divider + "call pe kya try karna hai" hints. */}
          <span className={styles.micPermissionDivider} aria-hidden="true" />

          <ul className={styles.micTryList}>
            {tryHints.map((hint) => (
              <li key={hint} className={styles.micTryItem}>
                <span className={styles.micTryDot} aria-hidden="true" />
                <span className={styles.micTryText}>{hint}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AgentCreationLoader;
